import { AdsUISettings } from "../../../components/ui/Simple/Ads/useAds";
import { FormButtonIconType } from "../../../components/ui/Simple/SimpleElements";
import { ReducerCommandType } from "../Types";
import { localStore } from "../io/localStore";

// ----------------------- Events -----------------------

const CHANGE_UI_SERVER_BUILD_EPOCH = "CHANGE_UI_SERVER_BUILD_EPOCH";
const CHANGE_UI_SERVER_BUILD_EPOCH_LASTCHECK = "CHANGE_UI_SERVER_BUILD_EPOCH_LASTCHECK";
const CHANGE_UI_NAVIGATE_TO_ELEMENTID = "CHANGE_UI_NAVIGATE_TO_ELEMENTID";
const CHANGE_UI_RESET = "CHANGE_UI_RESET";
const CHANGE_UI_APP_TEMPLATE = "CHANGE_UI_APP_TEMPLATE";
const CHANGE_UI_THEME = "CHANGE_UI_THEME";
const CHANGE_UI_LANGUAGE = "CHANGE_UI_LANGUAGE";
const CHANGE_UI_LEFT_MENU_STATE = "CHANGE_UI_LEFT_MENU_STATE";

const CHANGE_UI_ADS_UI_SETTINGS = "CHANGE_UI_ADS_UI_SETTINGS";
const CHANGE_UI_PLANTSEARCH = "CHANGE_UI_PLANTSEARCH";
const CHANGE_UI_DIALOGS = "CHANGE_UI_DIALOGS";
const CHANGE_UI_TOASTS = "CHANGE_UI_TOASTS";
const CHANGE_UI_ASSORTMENTS = "CHANGE_UI_ASSORTMENTS";
const CHANGE_UI_ADDINGNEWORGANIZATION = "CHANGE_UI_ADDINGNEWORGANIZATION";


// ----------------------- Types -----------------------
// Reducer

export type UITemplateType = "simple" | "pro";
export type UIReducerType = {
    serverBuildEpoch: number
    serverBuildEpochLastCheck: number
    navigateToElementId: string,
    appUI: UITemplateType
    theme: UIThemeNamesType,
    leftMenuState: LeftMenuStateType,
    language: LanguageType,
    adsUISettings: AdsUISettings,
    plantSearchUI: PlantSearchUIType,
    assortmentsUI: AssortmentsUIType,
    dialogs: BaseDialogType | undefined,
    toasts: BaseToastType | undefined,
    addingNewOrganization?: AddingNewOrganizationType
}
export type AddingNewOrganizationType = {
    isAdding?: boolean,
    newOrganizationName?: string,
    newOrganizationEmail?: string,
    addingViewState?: AddingNewOrganizationViewStateType
}
export type AddingNewOrganizationViewStateType = "createaccount" | "accountcreated" | "login";
export type BaseDialogType = {
    type: BaseDialogTypeType
    header: string,
    text: string,
    visible: boolean,
    okText?: string,
    okIcon?: FormButtonIconType,
    cancelText?: string
    onOk?(): void
    onCancel?(): void
}
export type BaseToastType = {
    text: string
}

export type BaseDialogOptionsType = {
    header?: string,
    text?: string,
    okText?: string
    okIcon?: FormButtonIconType
    cancelText?: string
}
export type BaseDialogTypeType = "info" | "warning" | "confirm" | "input-link" | "add" | "proactivated";

export type PlantSearchUIType = {
    planttarget?: PlantSearchUITarget
}
export type PlantSearchUITarget = {
    type?: PlantSearchUITargetType,
    id?: string,
    plantCart?: string[],
    cartMaxSize?: number
}
export type AssortmentsUIType = {
    uiTabState: AssortmentsUITabStateType
}
export type AssortmentsUITabStateType = "offer" | "selection" | "plants";

export type PlantSearchUITargetType = "none" | "ad" | "assortment";
// -------------------------- Theme --------------------------
export type UIThemeNamesType = "theme-default" | "theme-dark";
export const ThemeNames: { dark: UIThemeNamesType, default: UIThemeNamesType } = {
    dark: "theme-dark",
    default: "theme-default"
}
export const StorageKey_ui_settings_theme = "ui_settings_theme";
const _themeFromLocal = localStore.get<UIThemeNamesType>(StorageKey_ui_settings_theme);

// -------------------------- LeftMenu --------------------------
export type LeftMenuStateType = "open" | "closed";

// -------------------------- Language --------------------------
export type LanguageType = "en" | "sv";
export type LanguageItemType = { id: LanguageType, name: string };
export const StorageKey_ui_settings_language = "ui_settings_language";
// Get From browser:
const browserLanguage = navigator.language?.split('-')?.[0];
const browserLang = ["sv", "en"].indexOf(browserLanguage) >= 0 ? browserLanguage : null;
const _languageFromLocal: LanguageType = localStore.get<LanguageType>(StorageKey_ui_settings_language) || browserLang;

// ----------------------- DefaultState -----------------------
const uiReducerDefaultState: UIReducerType = {
    serverBuildEpoch: 0,
    serverBuildEpochLastCheck: 0,
    navigateToElementId: "",
    appUI: "simple",
    theme: (_themeFromLocal === ThemeNames.dark || _themeFromLocal === ThemeNames.default) ? _themeFromLocal : ThemeNames.default,
    leftMenuState: "open",
    language: _languageFromLocal ?? "en",
    adsUISettings: {
        selectedTab: undefined,
        editingIds: {},
        editModels: {},
        processing: {}
    },
    assortmentsUI: {
        uiTabState: "offer"
    },
    plantSearchUI: {},
    dialogs: undefined,
    toasts: undefined,
    addingNewOrganization: undefined
}

// ----------------------- Reducer -----------------------
const uIReducer = (state = { ...uiReducerDefaultState }, action: ReducerCommandType) => {
    switch (action.type) {
        case CHANGE_UI_RESET: { state = { ...uiReducerDefaultState }; break; }
        case CHANGE_UI_SERVER_BUILD_EPOCH: { state = { ...state, serverBuildEpoch: action.payload }; break; }
        case CHANGE_UI_SERVER_BUILD_EPOCH_LASTCHECK: { state = { ...state, serverBuildEpochLastCheck: action.payload }; break; }
        case CHANGE_UI_NAVIGATE_TO_ELEMENTID: { state = { ...state, navigateToElementId: action.payload }; break; }
        case CHANGE_UI_APP_TEMPLATE: { state = { ...state, appUI: action.payload }; break; }
        case CHANGE_UI_THEME: { state = { ...state, theme: action.payload }; break; }
        case CHANGE_UI_LANGUAGE: { state = { ...state, language: action.payload }; break; }
        case CHANGE_UI_LEFT_MENU_STATE: { state = { ...state, leftMenuState: action.payload }; break; }
        case CHANGE_UI_ADS_UI_SETTINGS: { state = { ...state, adsUISettings: action.payload }; break; }
        case CHANGE_UI_PLANTSEARCH: { state = { ...state, plantSearchUI: action.payload }; break; }
        case CHANGE_UI_DIALOGS: { state = { ...state, dialogs: action.payload }; break; }
        case CHANGE_UI_TOASTS: { state = { ...state, toasts: action.payload }; break; }
        case CHANGE_UI_ASSORTMENTS: { state = { ...state, assortmentsUI: action.payload }; break; }
        case CHANGE_UI_ADDINGNEWORGANIZATION: { state = { ...state, addingNewOrganization: action.payload }; break; }

        default: { return state; }
    }
    return state;
};

// ----------------------- Export -----------------------
export {
    uIReducer,
    CHANGE_UI_SERVER_BUILD_EPOCH,
    CHANGE_UI_SERVER_BUILD_EPOCH_LASTCHECK,
    CHANGE_UI_NAVIGATE_TO_ELEMENTID,
    CHANGE_UI_RESET,
    CHANGE_UI_APP_TEMPLATE,
    CHANGE_UI_THEME,
    CHANGE_UI_LANGUAGE,
    CHANGE_UI_LEFT_MENU_STATE,
    CHANGE_UI_ADS_UI_SETTINGS,
    CHANGE_UI_PLANTSEARCH,
    CHANGE_UI_DIALOGS,
    CHANGE_UI_TOASTS,
    CHANGE_UI_ASSORTMENTS,
    CHANGE_UI_ADDINGNEWORGANIZATION
}